import { CheckIcon } from "@xola/icons";
import { almostZero, Button, Label, Table } from "@xola/ui-kit";
import clsx from "clsx";
import {
    DateFormat,
    formatDate,
    getExperiencePurchaseItemsHavingAffiliate,
    getItemLineItemsByType,
    getLineItemsByCode,
    getPurchaseLineItemsByType,
} from "common";
import { sumBy } from "lodash";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { ExperiencePurchaseItem, LineItemCode, LineItemType, Partner, Purchase } from "types";
import { getVoucherCode } from "../../../../models/Ticket";
import { Currency } from "../../../Common/Currency";
import { PurchaseProductImage } from "../../../PurchasesMenu/PurchaseProductImage";
import { PaymentSettlementData, PaymentSettlementModal, SettlementType } from "../PaymentSettlementModal";

interface PartnerStatementListProps {
    purchases: Purchase[];
    partner: Partner;
}

export const PartnerStatementList = ({ purchases, partner }: PartnerStatementListProps) => {
    const history = useHistory();
    const handleClick = (id?: ID) => (id ? history.push(`/purchases/${id}`) : null);
    const [selectedPurchases, setSelectedPurchases] = useState<ID[]>([]);

    let totalNetPrice = 0;
    let totalCommission = 0;
    let totalDeposit = 0;
    let totalDue = 0;
    let totalDueNotReconciled = 0;

    const [paymentSettlementData, setPaymentSettlementData] = useState<PaymentSettlementData>();

    const handleReconcileClick = (balanceDue: number) => {
        const due = selectedPurchases.length > 0 ? getBalanceDueForSelectedPurchases() : balanceDue;
        const paymentSettlementType = due > 0 ? SettlementType.PAY : SettlementType.COLLECT;
        setPaymentSettlementData({
            partnerId: partner.id,
            balanceDue: due,
            paymentSettlementType: paymentSettlementType,
            children: (partner.children?.length ?? 0) > 0,
            purchases: selectedPurchases.length > 0 ? selectedPurchases : undefined,
        });
    };

    const handlePurchaseClick = (purchaseId: ID, isReconciled: boolean, totalDueNotReconciled: number) => {
        if (!isReconciled && totalDueNotReconciled !== 0) {
            if (selectedPurchases.includes(purchaseId)) {
                setSelectedPurchases(selectedPurchases.filter((id) => id !== purchaseId));
            } else {
                setSelectedPurchases([...selectedPurchases, purchaseId]);
            }
        }
    };

    const getBalanceDueForSelectedPurchases = () => {
        return sumBy(selectedPurchases, (purchaseId) => {
            const purchase = purchases.find((purchase) => purchase.id === purchaseId);
            const items = getExperiencePurchaseItemsHavingAffiliate(purchase!);
            const { due } = getCommissionAmounts(purchase!, items);
            return due;
        });
    };

    return (
        <div className="flex h-auto w-full flex-col px-6 pt-3">
            <Label class="pb-2 text-lg font-bold print:hidden" data-testid="partner-outstanding-commissions-label">
                Outstanding Commissions
            </Label>
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header>Status</Table.Header>
                        <Table.Header>Item</Table.Header>
                        <Table.Header>Date</Table.Header>
                        <Table.Header>Customer</Table.Header>
                        <Table.Header>Partner</Table.Header>
                        <Table.Header>Voucher</Table.Header>
                        <Table.Header className="text-right">Net Price</Table.Header>
                        <Table.Header className="text-right">Commission</Table.Header>
                        <Table.Header className="text-right">Partner Deposit</Table.Header>
                        <Table.Header className="text-right">Commission Due</Table.Header>
                    </Table.Row>
                </Table.Head>

                <Table.Body isStriped>
                    {purchases.map((purchase) => {
                        const items = getExperiencePurchaseItemsHavingAffiliate(purchase);
                        const isReconciled =
                            "affiliatePayment" in purchase && Math.abs(purchase.affiliatePayment ?? 0) >= 0;

                        const isSelected = selectedPurchases.includes(purchase.id);
                        const allTickets = items.flatMap((item) => item.tickets ?? []);
                        const { netPrice, commission, deposit, due } = getCommissionAmounts(purchase, items);
                        const firstArrivalDate = formatDate(items[0].arrivalDateTime, DateFormat.DATE_SHORT);

                        totalNetPrice += netPrice;
                        totalCommission += commission;
                        totalDeposit += deposit;
                        totalDue += due;
                        totalDueNotReconciled += isReconciled ? 0 : due;

                        return (
                            <Table.Row
                                key={purchase.id}
                                class={clsx("cursor-pointer", isReconciled || (isSelected && "bg-blue-lighter"))}
                                onClick={() => handlePurchaseClick(purchase.id, isReconciled, totalDueNotReconciled)}
                            >
                                <Table.Cell className="text-center">
                                    {isReconciled && <CheckIcon size="medium" />}
                                </Table.Cell>
                                <Table.Cell
                                    className="min-w-20 cursor-pointer"
                                    onClick={() => handleClick(purchase.id)}
                                >
                                    <PurchaseProductImage purchase={purchase} />
                                </Table.Cell>
                                <Table.Cell className="no-translate">{firstArrivalDate}</Table.Cell>
                                <Table.Cell className="no-translate">{purchase.customerName}</Table.Cell>
                                <Table.Cell className="no-translate">{partner.name}</Table.Cell>
                                <Table.Cell className="no-translate">
                                    {allTickets.length > 0 ? getVoucherCode(allTickets, "--") : "--"}
                                </Table.Cell>
                                <CurrencyCell amount={netPrice} />
                                <CurrencyCell amount={commission} />
                                <CurrencyCell amount={deposit} />
                                <CurrencyCell amount={due} />
                            </Table.Row>
                        );
                    })}

                    <Table.Row class="bg-white font-bold">
                        <Table.Cell colSpan="5" />
                        <Table.Cell
                            className="py-4 text-right !text-black"
                            data-testid="partner-outstanding-commissions-total"
                        >
                            Total
                        </Table.Cell>
                        <CurrencyCell amount={totalNetPrice} />
                        <CurrencyCell amount={totalCommission} />
                        <CurrencyCell amount={totalDeposit} />
                        <CurrencyCell amount={totalDue} />
                    </Table.Row>

                    <Table.Row class="font-bold">
                        <Table.Cell colSpan="5" />
                        <Table.Cell className="py-4 text-right !text-black" data-testid="partner-reconcile-payments">
                            Reconcile Payments
                        </Table.Cell>
                        <Table.Cell colSpan="3" />
                        <CurrencyCell amount={totalDue - totalDueNotReconciled} />
                    </Table.Row>
                </Table.Body>
            </Table>

            <ReconcileBalance balanceDue={totalDueNotReconciled} onReconcileClick={handleReconcileClick} />

            <PaymentSettlementModal
                paymentSettlementData={paymentSettlementData}
                isOpen={!!paymentSettlementData}
                onClose={() => {
                    setPaymentSettlementData(undefined);
                    setSelectedPurchases([]);
                }}
            />
        </div>
    );
};

const getCommissionAmounts = (purchase: Purchase, items: ExperiencePurchaseItem[]) => {
    const feeItems = items.map((item) => getLineItemsByCode(item, LineItemCode.FEE));
    const taxItems = items.map((item) => getLineItemsByCode(item, LineItemCode.TAX));
    const feesAmount = sumBy(feeItems.flat(), "amount");
    const taxAmount = sumBy(taxItems.flat(), "amount");
    // As per XOL-2420, Net Price: (Total Amount - Taxes). In v2, we have both taxes and fees
    // so Net Price: (Total Amount - Taxes - Fees)
    const netPrice = purchase.amount - feesAmount - taxAmount;

    const commissionItems = items.map((item) => getItemLineItemsByType(item, LineItemType.AFFILIATE_COMMISSION));
    const commission = sumBy(commissionItems.flat(), "amount");
    const deposit = -sumBy(getPurchaseLineItemsByType(purchase, LineItemType.AFFILIATE_DEPOSIT), "amount");

    const due = almostZero(commission - deposit) ? 0 : commission - deposit;
    return { netPrice, commission, deposit, due };
};

interface ReconcileBalanceProps {
    balanceDue: number;
    onReconcileClick: (balanceDue: number) => void;
}

const ReconcileBalance = ({ balanceDue, onReconcileClick }: ReconcileBalanceProps) => {
    return (
        <>
            <div className="flex justify-end p-2">
                <Label className={clsx("text-lg font-bold", balanceDue < 0 ? "text-green" : "text-red")}>
                    <span className="pr-5">Balance to {balanceDue < 0 ? "Pay" : "Collect"}</span>
                    <Currency>{balanceDue}</Currency>
                </Label>
            </div>

            {!almostZero(balanceDue) && (
                <div className="flex justify-end pr-2 print:hidden">
                    <Button size="small" onClick={() => onReconcileClick(balanceDue)}>
                        Reconcile
                    </Button>
                </div>
            )}
        </>
    );
};

const CurrencyCell = ({ amount }: { amount: number }) => {
    return (
        <Table.Cell className="text-right">
            <Currency>{amount}</Currency>
        </Table.Cell>
    );
};
